
import { Component, Vue } from "vue-property-decorator";
import Moment from "moment";
import httpClient from "@/store/httpClient";
import { OfferRequestDto } from "@/dtos/offerRequestDto";
import { OfferState } from "@/dtos/offerState";
import { OfferRequestCallOffInterval } from "@/dtos/offerRequestCallOffInterval";
import { OfferRequestPositionDto } from "@/dtos/offerRequestPositionDto";

@Component
export default class OfferRequests extends Vue {
	private requests: OfferRequestDto[] = new Array<OfferRequestDto>();
	private isLoading: boolean = false;
	private deleteDialogOpen: boolean = false;
	private isDeleting: boolean = false;
	private requestToDelete: OfferRequestDto | null = null;

	// Keys of the locale entries below offerRequest.useCases and offerRequest.steps.
	// Kept here so the template does not repeat the same markup three times.
	private useCases = [
		{ key: "bulk", icon: "mdi-package-variant-closed" },
		{ key: "series", icon: "mdi-repeat-variant" },
		{ key: "automation", icon: "mdi-robot-industrial" },
		{ key: "individual", icon: "mdi-account-tie-voice" },
	];
	private steps = ["step1", "step2", "step3"];

	private mounted() {
		this.loadRequests();
	}

	private get hasRequests(): boolean {
		return this.requests.length > 0;
	}

	// Number of article positions in the current cart. The cart is null until the
	// store has initialised it, see setupShoppingCart in App.vue.
	private get cartItemCount(): number {
		const cart = this.$store.state.currentCart;
		if (cart === null || cart === undefined) {
			return 0;
		}
		return cart.cartItems.length;
	}

	private get hasCartItems(): boolean {
		return this.cartItemCount > 0;
	}

	/** Loads the offer requests of the current customer, newest first. */
	private async loadRequests() {
		this.isLoading = true;
		try {
			const response = await httpClient().get("Offer/GetOfferRequests");
			this.requests = response.data;
		} catch (e) {
			console.error("Failed to load the offer requests.", e);
			this.$store.dispatch("setSnackbarErrorText", this.$t("offerRequest.list.loadFailed"));
			this.requests = new Array<OfferRequestDto>();
		} finally {
			this.isLoading = false;
		}
	}

	private createRequest() {
		this.$router.push({ name: "CreateOfferRequest" });
	}

	/** Only a request the customer has not sent yet can still be changed. */
	private isEditable(request: OfferRequestDto): boolean {
		return request.state === OfferState.Draft;
	}

	/**
	 * Opens a draft for editing. A request that is already with Blexon cannot be
	 * changed, so the tile does nothing for those.
	 */
	private openRequest(request: OfferRequestDto) {
		if (!this.isEditable(request)) {
			return;
		}
		this.$router.push({
			name: "EditOfferRequest",
			params: { requestId: String(request.id) },
		});
	}

	private askToDelete(request: OfferRequestDto) {
		this.requestToDelete = request;
		this.deleteDialogOpen = true;
	}

	/** Removes the draft and its positions, then refreshes the list. */
	private async confirmDelete() {
		if (this.requestToDelete === null || this.isDeleting) {
			return;
		}
		this.isDeleting = true;
		try {
			await httpClient().post(`Offer/DeleteOfferRequest?id=${this.requestToDelete.id}`);
			this.$store.dispatch("setSnackbarText", this.$t("offerRequest.form.deleted"));
			this.deleteDialogOpen = false;
			this.requestToDelete = null;
			await this.loadRequests();
		} catch (e) {
			console.error("Failed to delete the offer request.", e);
			this.$store.dispatch("setSnackbarErrorText", this.$t("offerRequest.form.deleteFailed"));
		} finally {
			this.isDeleting = false;
		}
	}

	private formatDate(value: Date): string {
		return Moment(String(value)).format("DD.MM.YYYY");
	}

	// Tile figures are derived from the positions, so they cannot drift apart from them.
	private positionCount(request: OfferRequestDto): number {
		return request.positions.length;
	}

	private totalQuantity(request: OfferRequestDto): number {
		return request.positions.reduce(
			(sum: number, position: OfferRequestPositionDto) => sum + position.quantity,
			0
		);
	}

	private isRecurring(request: OfferRequestDto): boolean {
		return request.callOffInterval !== OfferRequestCallOffInterval.Once;
	}

	private stateLabel(state: OfferState): string {
		switch (state) {
			case OfferState.Draft:
				return this.$t("offerRequest.states.draft").toString();
			case OfferState.WaitingForManualOffer:
				return this.$t("offerRequest.states.waitingForManualOffer").toString();
			case OfferState.WaitingForCustomerAnswer:
				return this.$t("offerRequest.states.waitingForCustomerAnswer").toString();
			case OfferState.Accepted:
				return this.$t("offerRequest.states.accepted").toString();
			case OfferState.Declined:
				return this.$t("offerRequest.states.declined").toString();
			default:
				return this.$t("offerRequest.states.unknown").toString();
		}
	}

	// Colour of the left rail and of the state chip. Yellow marks a step that is
	// currently with Blexon, green a result the customer can act on, grey a closed request.
	private stateColor(state: OfferState): string {
		switch (state) {
			case OfferState.Draft:
				return "#9aa1a9";
			case OfferState.WaitingForManualOffer:
				return "#efbb20";
			case OfferState.WaitingForCustomerAnswer:
				return "#2f855a";
			case OfferState.Accepted:
				return "#1f5c3f";
			default:
				return "#c8ccd1";
		}
	}

	private stateChipStyle(state: OfferState) {
		const color = this.stateColor(state);
		return {
			color: color,
			borderColor: color,
		};
	}
}
